'use client';

import * as React from 'react';
import YouTube from 'react-youtube';
import { cn } from '@workspace/ui/lib/utils';

interface YouTubeEmbedProps {
  videoId: string;
  title?: string;
  className?: string;
}

export function YouTubeEmbed({ videoId, title, className }: YouTubeEmbedProps) {
  const [error, setError] = React.useState(false);

  // Extract video ID from URL if a full URL is provided
  const getVideoId = React.useMemo(() => {
    if (!videoId) return '';

    // If it's already just an ID (no slashes, no query params), return it
    if (!videoId.includes('/') && !videoId.includes('?')) {
      return videoId;
    }

    // Handle youtu.be short URLs
    if (videoId.includes('youtu.be/')) {
      const match = videoId.match(/youtu\.be\/([^?&]+)/);
      return match ? match[1] : videoId;
    }

    // Handle youtube.com/watch?v= format
    if (videoId.includes('youtube.com')) {
      const match = videoId.match(/[?&]v=([^&]+)/);
      return match ? match[1] : videoId;
    }

    return videoId;
  }, [videoId]);

  const opts = React.useMemo(() => ({
    width: '100%',
    height: '100%',
    playerVars: {
      // Removed autoplay to avoid Error 153 - user can click play
      rel: 0,
      modestbranding: 1,
      playsinline: 1,
    },
  }), []);

  if (!getVideoId) {
    return null;
  }

  return (
    <div className={cn('relative w-full aspect-video mx-auto my-6 rounded-lg border border-gray-200 overflow-hidden', className)}>
      {error ? (
        <div className="flex flex-col items-center justify-center h-full bg-muted rounded-lg p-6">
          <p className="text-muted-foreground mb-4 text-center">
            Unable to load video. The video may not allow embedding.
          </p>
          <a
            href={`https://www.youtube.com/watch?v=${getVideoId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Watch on YouTube →
          </a>
        </div>
      ) : (
        <YouTube
          videoId={getVideoId}
          opts={opts}
          className="w-full h-full"
          iframeClassName="w-full h-full rounded-lg"
          onError={() => setError(true)}
          title={title}
        />
      )}
    </div>
  );
}

